// src/components/ProtectedRoute.jsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasRole } from '../config/roles';

/**
 * Route guard.
 *
 * - No `allowedRoles` and no `allowedPositions`: any signed-in user.
 * - Only one of them: the user must satisfy it.
 * - Both: the user must satisfy *either*. Some pages are reachable two ways —
 *   a department page belongs to a manager (by position) but an IT Admin needs
 *   to get in too (by role). Requiring both would lock out everyone.
 *
 * This is UX, not security: it runs in the browser and can be bypassed. The
 * real boundary is the Realtime DB rules in database.rules.json.
 */
const ProtectedRoute = ({ children, allowedRoles, allowedPositions }) => {
    const { user, role, position } = useAuth();

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    const roleRequired = Array.isArray(allowedRoles) && allowedRoles.length > 0;
    const positionRequired = Array.isArray(allowedPositions) && allowedPositions.length > 0;

    if (roleRequired || positionRequired) {
        const roleOk = roleRequired && hasRole(role, allowedRoles);
        const positionOk = positionRequired && allowedPositions.includes(position);

        if (!roleOk && !positionOk) {
            return <Navigate to="/unauthorized" replace />;
        }
    }

    return children;
};

export default ProtectedRoute;
